import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  JSX,
} from 'react'

import { ConfigProvider } from 'antd'
import { Locale as AntLocale } from 'antd/lib/locale-provider'
import en_GB from 'antd/lib/locale-provider/en_GB'
import en_US from 'antd/lib/locale-provider/en_US'
import es_ES from 'antd/lib/locale-provider/es_ES'
import { TOptions as I18NTOptions } from 'i18next'
import { CountryCode } from 'libphonenumber-js'
import moment from 'moment-timezone'
import { Trans, useTranslation } from 'react-i18next'

import 'moment/locale/en-au'
import 'moment/locale/en-gb'

import config from '../config'

import { enAU } from '@/utils/jargon/en-AU'
import { enCA } from '@/utils/jargon/en-CA'
import { enGB } from '@/utils/jargon/en-GB'
import { enUS } from '@/utils/jargon/en-US'
import { esAU } from '@/utils/jargon/es-AU'
import { esCA } from '@/utils/jargon/es-CA'
import { esGB } from '@/utils/jargon/es-GB'
import { esUS } from '@/utils/jargon/es-US'
import {
  JargonTranslation,
  JargonTranslationPath,
} from '@/utils/jargon/jargonTypes'

import { SupportedLocaleCountryCode } from 'types/api.graphql'

export enum Locale {
  enUS = 'en-US',
  enGB = 'en-GB',
  enAU = 'en-AU',
  esUS = 'es-US',
  esGB = 'es-GB',
  esAU = 'es-AU',
  enCA = 'en-CA',
  esCA = 'es-CA',
}

export enum LocaleCountry {
  'United Kingdom' = 'GB',
  'United States' = 'US',
  Australia = 'AU',
  Canada = 'CA',
}

export enum Language {
  English = 'en',
  Spanish = 'es',
}

enum Features {
  AbnLookup = 'abnLookup',
  SwmsBuilder = 'swmsBuilder',
  MobileApp = 'mobileApp',
  MultiLanguage = 'multiLanguage',
}

type TBuildPassOptions = {
  projectName: string
  builderName?: string
  userName?: string
}

// Translation options excluding jargon paths and BuildPass options
type TOptions = Omit<
  I18NTOptions,
  keyof JargonTranslationPath | keyof TBuildPassOptions
> & {
  [K in JargonTranslationPath | keyof TBuildPassOptions]?: never
}

type TMemoValue = string | React.ReactElement

type LocaleProviderProps = {
  children: ReactNode
  buildPassOptions?: TBuildPassOptions
  localeCountryCode?: SupportedLocaleCountryCode
  skipSettingMomentLocale?: boolean
}

export type LocaleContextValue = {
  t: (path: string, options?: TOptions) => string
  tComponent: (path: string, options?: TOptions) => JSX.Element
  tLanguage: Language
  tChangeLanguage: (lang: Language) => void
  locale: Locale | null
  unit: Unit
  temperatureUnit: TemperatureUnit
  setLocale: (locale: Locale) => void
  setBuildPassOptions: Dispatch<SetStateAction<TBuildPassOptions>>
  countryCode: CountryCode | null
  language: Language | null
  features: Record<Features, boolean>
}

const FEATURES_BY_LOCALE: Record<
  LocaleContextValue['locale'],
  Record<Features, boolean>
> = {
  [Locale.enUS]: {
    [Features.AbnLookup]: false,
    [Features.SwmsBuilder]: true,
    [Features.MobileApp]: true,
    [Features.MultiLanguage]: true,
  },
  [Locale.enGB]: {
    [Features.AbnLookup]: false,
    [Features.SwmsBuilder]: false,
    [Features.MobileApp]: false,
    [Features.MultiLanguage]: false,
  },
  [Locale.enAU]: {
    [Features.AbnLookup]: true,
    [Features.SwmsBuilder]: true,
    [Features.MobileApp]: true,
    [Features.MultiLanguage]: false,
  },
  [Locale.enCA]: {
    [Features.AbnLookup]: false,
    [Features.SwmsBuilder]: true,
    [Features.MobileApp]: true,
    [Features.MultiLanguage]: true,
  },
  [Locale.esUS]: {
    [Features.AbnLookup]: false,
    [Features.SwmsBuilder]: true,
    [Features.MobileApp]: true,
    [Features.MultiLanguage]: true,
  },
  [Locale.esGB]: {
    [Features.AbnLookup]: false,
    [Features.SwmsBuilder]: false,
    [Features.MobileApp]: false,
    [Features.MultiLanguage]: true,
  },
  [Locale.esAU]: {
    [Features.AbnLookup]: true,
    [Features.SwmsBuilder]: true,
    [Features.MobileApp]: true,
    [Features.MultiLanguage]: true,
  },
  [Locale.esCA]: {
    [Features.AbnLookup]: false,
    [Features.SwmsBuilder]: true,
    [Features.MobileApp]: true,
    [Features.MultiLanguage]: true,
  },
}

const DEFAULT_FEATURES: Record<Features, boolean> = {
  [Features.AbnLookup]: true,
  [Features.SwmsBuilder]: true,
  [Features.MobileApp]: true,
  [Features.MultiLanguage]: false,
}

const LOCALES_BY_COUNTRY: Record<
  SupportedLocaleCountryCode,
  Record<Language, Locale>
> = {
  US: {
    [Language.English]: Locale.enUS,
    [Language.Spanish]: Locale.esUS,
  },
  CA: {
    [Language.English]: Locale.enCA,
    [Language.Spanish]: Locale.esCA,
  },
  GB: {
    [Language.English]: Locale.enGB,
    [Language.Spanish]: Locale.esGB,
  },
  AU: {
    [Language.English]: Locale.enAU,
    [Language.Spanish]: Locale.esAU,
  },
}

const ANT_LOCALES_BY_BP_LOCALE: Record<Locale, AntLocale> = {
  [Locale.enUS]: en_US,
  [Locale.enAU]: en_GB,
  [Locale.enGB]: en_GB,
  [Locale.enCA]: en_GB,
  [Locale.esUS]: es_ES,
  [Locale.esGB]: es_ES,
  [Locale.esAU]: es_ES,
  [Locale.esCA]: es_ES,
}

const MOMENT_LOCALES_BY_BP_LOCALE: Record<Locale, string> = {
  [Locale.enUS]: 'en',
  [Locale.enAU]: 'en-au',
  [Locale.enGB]: 'en-gb',
  [Locale.enCA]: 'en-gb',
  [Locale.esUS]: 'en',
  [Locale.esGB]: 'es-gb',
  [Locale.esAU]: 'es-au',
  [Locale.esCA]: 'es-gb',
}

export const JARGON_BY_LOCALE: Record<Locale, JargonTranslation> = {
  [Locale.enUS]: enUS,
  [Locale.enGB]: enGB,
  [Locale.enAU]: enAU,
  [Locale.enCA]: enCA,
  [Locale.esUS]: esUS,
  [Locale.esGB]: esGB,
  [Locale.esAU]: esAU,
  [Locale.esCA]: esCA,
}

export type Unit = 'metric' | 'imperial'

export const UNIT_BY_LOCALE: Record<Locale, Unit> = {
  [Locale.enUS]: 'imperial',
  [Locale.enGB]: 'metric',
  [Locale.enAU]: 'metric',
  [Locale.enCA]: 'metric',
  [Locale.esUS]: 'imperial',
  [Locale.esGB]: 'metric',
  [Locale.esAU]: 'metric',
  [Locale.esCA]: 'metric',
}

export type TemperatureUnit = '°C' | '°F'
export const TEMPERATURE_UNIT_BY_LOCALE: Record<Locale, TemperatureUnit> = {
  [Locale.enAU]: '°C',
  [Locale.enGB]: '°C',
  [Locale.enUS]: '°F',
  [Locale.enCA]: '°C',
  [Locale.esUS]: '°F',
  [Locale.esGB]: '°C',
  [Locale.esAU]: '°C',
  [Locale.esCA]: '°C',
} as const

export const LocaleContext = createContext<LocaleContextValue>({
  t: () => '',
  tComponent: () => null,
  tLanguage: Language.English,
  tChangeLanguage: () => {},
  locale: null,
  unit: null,
  temperatureUnit: null,
  setLocale: () => {},
  setBuildPassOptions: () => {},
  countryCode: null,
  language: null,
  features: DEFAULT_FEATURES,
})

export const getLocaleFromCountry = (
  localeCountry: SupportedLocaleCountryCode,
  language: string
): LocaleContextValue['locale'] => {
  return (
    LOCALES_BY_COUNTRY[localeCountry]?.[language] ||
    (config.defaultLocale as Locale)
  )
}

const getJargonsFromLocale = (locale: Locale): JargonTranslation => {
  const jargons = JARGON_BY_LOCALE[locale]

  if (!jargons) {
    throw new Error('Invalid jargon locale: ' + locale)
  }

  return jargons
}

const extractCountryCodeFromLocale = (
  locale: LocaleContextValue['locale']
): LocaleContextValue['countryCode'] => {
  return locale.split('-')[1] as LocaleContextValue['countryCode']
}

const extractLanguageCodeFromLocale = (
  locale: LocaleContextValue['locale']
): LocaleContextValue['language'] => {
  return locale?.split('-')[0] as LocaleContextValue['language']
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({
  children,
  localeCountryCode,
  buildPassOptions,
  skipSettingMomentLocale = false,
}) => {
  const { t: translate, i18n } = useTranslation()

  const [tBuildPassOptions, setBuildPassOptions] =
    useState<TBuildPassOptions>(buildPassOptions)

  useEffect(() => {
    setBuildPassOptions(buildPassOptions)
  }, [buildPassOptions])

  const [tLanguage, setTLanguage] = useState<Language>(
    extractLanguageCodeFromLocale(i18n.language as Locale) as Language
  )

  const [locale, setLocale] = useState(
    getLocaleFromCountry(localeCountryCode, tLanguage)
  )

  // Combine BuildPass options with jargon translations
  const tBaseOptions = useMemo<I18NTOptions>(() => {
    const jargons = getJargonsFromLocale(locale)
    return { ...tBuildPassOptions, ...jargons }
  }, [locale, tBuildPassOptions])

  const TMemoMap = useMemo(() => new Map<string, TMemoValue>(), [])

  const getMemoizedTValue = useCallback(
    <T,>(key: string, getValue: () => T): T => {
      if (TMemoMap.has(key)) {
        return TMemoMap.get(key) as T
      }

      const value = getValue()
      TMemoMap.set(key, value as TMemoValue)
      return value
    },
    [TMemoMap]
  )

  const t = useCallback(
    (path: string, options?: TOptions): string => {
      const mergedOptions: I18NTOptions = { ...options, ...tBaseOptions }
      const key = `${tLanguage}:${path}${JSON.stringify(mergedOptions)}`

      return getMemoizedTValue(key, () => translate(path, mergedOptions))
    },
    [tBaseOptions, tLanguage, getMemoizedTValue, translate]
  )

  const tComponent = useCallback(
    (path: string, options?: TOptions): JSX.Element => {
      const mergedOptions: I18NTOptions = { ...options, ...tBaseOptions }
      const key = `${tLanguage}:${path}${JSON.stringify(mergedOptions)}`

      return getMemoizedTValue(key, () => (
        <Trans i18nKey={path} values={mergedOptions} />
      ))
    },
    [tBaseOptions, tLanguage, getMemoizedTValue]
  )

  const tChangeLanguage = useCallback(
    (lang: Language) => {
      i18n.changeLanguage(lang).then(() => {
        setTLanguage(lang)
      })
    },
    [i18n]
  )

  useEffect(() => {
    // Set the locale when the country changes
    setLocale(getLocaleFromCountry(localeCountryCode, tLanguage))
  }, [localeCountryCode, tLanguage])

  useEffect(() => {
    if (locale && window && !skipSettingMomentLocale) {
      // Set the default locale for date-fns
      window['__localeId__'] = locale

      // Set defaults for moment.js (used by Antd)
      moment.locale(MOMENT_LOCALES_BY_BP_LOCALE[locale])
    }
  }, [locale, skipSettingMomentLocale])

  return (
    <ConfigProvider locale={ANT_LOCALES_BY_BP_LOCALE[locale]}>
      <LocaleContext.Provider
        value={{
          tLanguage,
          t,
          tComponent,
          tChangeLanguage,
          locale,
          setLocale,
          setBuildPassOptions,
          features: locale ? FEATURES_BY_LOCALE[locale] : DEFAULT_FEATURES,
          language: locale ? extractLanguageCodeFromLocale(locale) : null,
          countryCode: locale ? extractCountryCodeFromLocale(locale) : null,
          unit: locale ? UNIT_BY_LOCALE[locale] : 'metric',
          temperatureUnit: locale ? TEMPERATURE_UNIT_BY_LOCALE[locale] : '°C',
        }}
      >
        {children}
      </LocaleContext.Provider>
    </ConfigProvider>
  )
}

export const useLocale = () => useContext(LocaleContext)
